/**
 * Cloud Operations API Service
 *
 * API client for CPAL (Cloud Provider Abstraction Layer) endpoints.
 * Calls /api/admin/cloud/* endpoints built in WQ-093/111/112/124/125.
 *
 * @module services/cloudOpsService
 */

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

// =============================================================================
// Types
// =============================================================================

export interface ProviderInfo {
  provider: string;
  region: string;
  zone: string;
  account_id: string;
  detection_method: string;
  os: string;
  [key: string]: unknown;
}

export interface CloudHealth {
  status: string;
  provider: string;
  available_services: string[];
  [key: string]: unknown;
}

export interface IPAllocation {
  ip_address: string;
  allocation_type: string;
  status: string;
  label: string;
  provider_allocation_id: string;
  tenant_id?: string;
  created_at: string;
  released_at?: string;
  [key: string]: unknown;
}

export interface LBRule {
  rule_id: string;
  config_file: string;
  [key: string]: unknown;
}

export interface ComputeHealth {
  instance_state: string;
  instance_status: string;
  system_status: string;
  instance_id?: string;
  note?: string;
  [key: string]: unknown;
}

export interface DNSZone {
  zone_name: string;
  provider: string;
  record_count: number;
  [key: string]: unknown;
}

export interface Certificate {
  domain: string;
  status: string;
  expires: string;
  provider_id: string;
  [key: string]: unknown;
}

export interface CostCurrentMonth {
  total_usd: number;
  by_service?: Array<{ service: string; amount_usd: number; percent?: number }>;
  by_period?: Array<{ start: string; end: string; amount: number }>;
  currency?: string;
  note?: string;
  error?: string;
  [key: string]: unknown;
}

export interface ComplianceSummary {
  inherited_frameworks: Array<{ framework: string; scope: string; attestation_url?: string }>;
  partially_inherited?: Array<{ framework: string; scope: string }>;
  not_applicable?: string[];
  platform_must_prove: string[];
  controls?: Array<{
    framework: string;
    control_domain: string;
    scope: string;
    not_covered: string;
    attestation_url?: string;
  }>;
  [key: string]: unknown;
}

export interface ComplianceGap {
  framework: string;
  gaps: Array<{ area: string; description: string; severity: string }>;
  [key: string]: unknown;
}

export interface CloudOpsError {
  isError: true;
  status: number;
  message: string;
}

// =============================================================================
// Helper
// =============================================================================

async function cloudFetch<T>(
  authFetch: AuthFetch,
  url: string,
  method = 'GET',
): Promise<T | CloudOpsError> {
  try {
    const options: RequestInit = method === 'GET' ? {} : { method };
    const response = await authFetch(url, options);

    if (response.status === 501) {
      return { isError: true, status: 501, message: 'Not available on this provider' };
    }
    if (response.status === 503) {
      return { isError: true, status: 503, message: 'Cloud provider detection in progress...' };
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      return { isError: true, status: response.status, message: err.detail || `Error ${response.status}` };
    }

    return await response.json();
  } catch (err) {
    return { isError: true, status: 0, message: err instanceof Error ? err.message : 'Network error' };
  }
}

export function isCloudOpsError(val: unknown): val is CloudOpsError {
  return typeof val === 'object' && val !== null && (val as CloudOpsError).isError === true;
}

// =============================================================================
// API Functions
// =============================================================================

const CLOUD_BASE = '/api/admin/cloud';

export const getProviderInfo = (authFetch: AuthFetch) =>
  cloudFetch<ProviderInfo>(authFetch, `${CLOUD_BASE}/provider`);

export const getCloudHealth = (authFetch: AuthFetch) =>
  cloudFetch<CloudHealth>(authFetch, `${CLOUD_BASE}/health`);

export const forceDetect = (authFetch: AuthFetch) =>
  cloudFetch<ProviderInfo>(authFetch, `${CLOUD_BASE}/detect`, 'POST');

export const getIPAllocations = (authFetch: AuthFetch) =>
  cloudFetch<IPAllocation[]>(authFetch, `${CLOUD_BASE}/ip`);

export const getLBRules = (authFetch: AuthFetch) =>
  cloudFetch<LBRule[]>(authFetch, `${CLOUD_BASE}/lb/rules`);

export const getComputeHealth = (authFetch: AuthFetch) =>
  cloudFetch<ComputeHealth>(authFetch, `${CLOUD_BASE}/compute/health`);

export const getDNSZones = (authFetch: AuthFetch) =>
  cloudFetch<DNSZone[]>(authFetch, `${CLOUD_BASE}/dns/zones`);

export const getCertificates = (authFetch: AuthFetch) =>
  cloudFetch<Certificate[]>(authFetch, `${CLOUD_BASE}/certs/`);

export const getCostCurrentMonth = (authFetch: AuthFetch) =>
  cloudFetch<CostCurrentMonth>(authFetch, `${CLOUD_BASE}/cost/current-month`);

export const getComplianceSummary = (authFetch: AuthFetch) =>
  cloudFetch<ComplianceSummary>(authFetch, `${CLOUD_BASE}/compliance/summary`);

export const getComplianceGap = (authFetch: AuthFetch, framework: string) =>
  cloudFetch<ComplianceGap>(authFetch, `${CLOUD_BASE}/compliance/gap/${encodeURIComponent(framework)}`);

// =============================================================================
// Deploy Types & Functions (WQ-126a)
// =============================================================================

const DEPLOY_BASE = '/api/admin/deploy';

export interface DeployStatus {
  status: string;
  conclusion: string | null;
  created_at: string;
  html_url: string;
  head_sha: string;
}

export interface DeployRun {
  id: number;
  status: string;
  conclusion: string | null;
  created_at: string;
  updated_at: string;
  html_url: string;
  head_sha: string;
}

export interface DeployHealth {
  pat_configured: boolean;
  github_reachable: boolean;
  deployed_version: string;
  latest_version: string;
  up_to_date: boolean;
}

export const triggerDeploy = (authFetch: AuthFetch) =>
  cloudFetch<{ status: string; message: string }>(authFetch, `${DEPLOY_BASE}/trigger`, 'POST');

export const getDeployStatus = (authFetch: AuthFetch) =>
  cloudFetch<DeployStatus | null>(authFetch, `${DEPLOY_BASE}/status`);

export const getDeployHistory = (authFetch: AuthFetch, limit = 5) =>
  cloudFetch<DeployRun[]>(authFetch, `${DEPLOY_BASE}/history?limit=${limit}`);

export const getDeployHealth = (authFetch: AuthFetch) =>
  cloudFetch<DeployHealth>(authFetch, `${DEPLOY_BASE}/health`);

// =============================================================================
// Compute Size Advisory (WQ-332 / DM-28) — prod resize-in-place
// =============================================================================
//
// Advisory-only backing for the "Compute Size" panel: current instance type,
// measured tier headroom, the resize ladder with per-member fit, and a
// cost-minimizing recommended size. v1 is advise-only — the operator dispatches
// the resize-prod.yml workflow in GitHub Actions (behind the Environment gate);
// nothing here calls EC2.

const SLM_BASE = '/api/admin/ai/slm-deployments';

// GitHub Actions page for the resize workflow (the "dispatch link", DEF-015).
export const RESIZE_WORKFLOW_URL =
  'https://github.com/ORYANFIREX/ai-platform-golden-image/actions/workflows/resize-prod.yml';

export interface LadderMember {
  instance_type: string;
  total_ram_mb: number;
  estimated_free_ram_mb: number | null;
  tiers_fit: Record<string, boolean>;
  is_current: boolean;
}

export interface ComputeSizeAdvisory {
  current_instance_type: string | null;
  on_ladder: boolean;
  available_ram_mb: number | null;
  thresholds: { large_min_free_ram_mb: number; small_min_free_ram_mb: number };
  stack_reserved_mb: number;
  measured_tiers_fit: Record<string, boolean>;
  active_served_size_classes: string[];
  ladder: LadderMember[];
  recommended_size_for_large: string | null;
  gpu_excluded: boolean;
  gpu_note: string;
}

export const getComputeSizeAdvisory = (authFetch: AuthFetch) =>
  cloudFetch<ComputeSizeAdvisory>(authFetch, `${SLM_BASE}/compute-size-advisory`);
